import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const CHECKBOX_SIZE = 21;
const CHECKBOX_CHECK_SIZE = 12;

export const SquareCheckedIcon = () => (
  <Box
    sx={{
      width: CHECKBOX_SIZE,
      height: CHECKBOX_SIZE,
      border: '2px solid currentColor',
      position: 'relative',
      borderRadius: 1,
      m: '1.5px',
    }}
  >
    <Box
      sx={{
        width: CHECKBOX_CHECK_SIZE,
        height: CHECKBOX_CHECK_SIZE,
        position: 'absolute',
        bgcolor: 'currentColor',
        top: '50%',
        left: '50%',
        borderRadius: 0.5,
        transform: 'translate(-50%, -50%)',
      }}
    />
  </Box>
);

export const SquareUncheckedIcon = () => (
  <Box
    sx={{
      width: CHECKBOX_SIZE,
      height: CHECKBOX_SIZE,
      border: '2px solid currentColor',
      position: 'relative',
      borderRadius: 1,
      m: '1.5px',
    }}
  />
);

const CheckboxGroupWrapper = styled(Box)({
  width: '100%',
  margin: 0,
  display: 'flex',
});

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

const StyledFormGroup = styled(FormGroup, {
  shouldForwardProp: (prop) => prop !== 'row',
})<{ row?: boolean }>(({ theme, row }) => ({
  ...(row
    ? {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        '& .MuiFormControlLabel-root': {
          marginRight: theme.spacing(2),
          marginBottom: 0,
          minWidth: 'auto',
          [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(1),
          },
        },
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        '& .MuiFormControlLabel-root': {
          margin: 0,
        },
      }),
  marginBottom: 0,
}));

const ValidationBox = styled(Box)({
  minHeight: '20px',
  marginTop: 0,
});

export interface MultiSelectCheckboxOption<T extends string> {
  value: T;
  label: string;
}

interface MultiSelectCheckboxGroupProps<T extends string> {
  label: string;
  options: MultiSelectCheckboxOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  required?: boolean;
  errorText?: string;
  direction?: 'row' | 'column';
}

export const MultiSelectCheckboxGroup = <T extends string>({
  label,
  options,
  value,
  onChange,
  required = false,
  errorText,
  direction = 'column',
}: MultiSelectCheckboxGroupProps<T>) => {
  const handleChange =
    (optionValue: T) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      let next = value;

      if (checked) {
        if (!next.includes(optionValue)) {
          next = [...next, optionValue];
        }
      } else {
        next = next.filter((v) => v !== optionValue);
      }

      onChange(next);
    };

  const error = !!errorText;

  return (
    <CheckboxGroupWrapper>
      <StyledFormControl required={required} error={error} fullWidth>
        <FormLabel required={required} error={error}>
          {label}
        </FormLabel>
        <StyledFormGroup row={direction === 'row'}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  icon={<SquareUncheckedIcon />}
                  checkedIcon={<SquareCheckedIcon />}
                  checked={value.includes(option.value)}
                  onChange={handleChange(option.value)}
                />
              }
              label={option.label}
            />
          ))}
        </StyledFormGroup>
        <ValidationBox>
          <FormHelperText error={error}>{errorText || ' '}</FormHelperText>
        </ValidationBox>
      </StyledFormControl>
    </CheckboxGroupWrapper>
  );
};
